import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CloseButton,
  CloseButtonSize,
  FontWeight,
  Heading as HeadingDS,
  LiliButton,
  LiliButtonType,
} from '@lili/lili-frontend-common';
import { twMerge } from 'tailwind-merge';
import Heading from '../../../generic-components/Heading';
import { LiliPlanType, STYLES, TEXT } from '../constants';
import { PlanView } from './PlanView/PlanView';
import { ILiliPlan } from '../types';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { FlowContext } from '../index';
import { planReportName } from '../service';
import PlansAnnualSwitcher from './PlansAnnualSwitcher';
import { useStore } from '../../../store';

const {
  INDEX: { PLAN_PICKER },
} = STYLES;

interface IProps {
  currentPlan: string;
  availablePlans: ILiliPlan[];
  planTypeToShow?: string;
  onCloseClick: () => void;
  onUpgradeClick: (plan: ILiliPlan, isAnnual: boolean) => void;
  promotionType?: string;
  origin: string;
}

const PlanPicker = ({
  onCloseClick,
  onUpgradeClick,
  currentPlan,
  availablePlans,
  planTypeToShow,
  promotionType,
  origin,
}: IProps) => {
  const flowContext = useContext(FlowContext);
  const { mutateReportState, reportsState } = flowContext;

  const extractSelectedPlan = () => {
    const selectedPlan: ILiliPlan | undefined = availablePlans.find((plan: ILiliPlan) => plan.type === planTypeToShow);
    if (!selectedPlan) {
      return availablePlans[0];
    }
    return selectedPlan;
  };
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<ILiliPlan>(extractSelectedPlan());
  const {
    customerProfileInfo: { annual: isAnnualPlanPreselected },
  } = useStore((state) => state.summary);
  const [isAnnual, setIsAnnual] = useState(isAnnualPlanPreselected);

  const onCloseClickHandler = () => {
    trackMixPanel('Act on Lili Plans Full Offer Screen', {
      Action: 'Dismiss',
      'Current Plan': planReportName(currentPlan),
      Annual: isAnnual,
    });
    onCloseClick();
  };

  const onTabSelectedHandler = (plan: ILiliPlan) => {
    setSelectedPlan(plan);
    let reportValue = '';
    switch (plan.type) {
      case LiliPlanType.PRO: {
        reportValue = 'View Pro';
        break;
      }
      case LiliPlanType.PRO_V2: {
        reportValue = 'View Pro';
        break;
      }
      case LiliPlanType.SMART: {
        reportValue = 'View Smart';
        break;
      }
      case LiliPlanType.PREMIUM: {
        reportValue = 'View Premium';
        break;
      }
      default: {
        break;
      }
    }
    trackMixPanel('Act on Lili Plans Full Offer Screen', {
      Action: reportValue,
      'Current Plan': planReportName(currentPlan),
      Annual: isAnnual,
    });
  };
  const onUpgradeClickHandler = (plan: ILiliPlan) => {
    onUpgradeClick(plan, isAnnual);
    trackMixPanel('Act on Lili Plans Full Offer Screen', {
      Action: 'Upgrade Now',
      'Current Plan': planReportName(currentPlan),
      Annual: isAnnual,
    });
  };
  useEffect(() => {
    if (!reportsState.didReportPickerView) {
      trackMixPanel('View Lili Plans Full Offer Screen', {
        Origin: origin,
        PromotionType: promotionType,
        'Current Plan': planReportName(currentPlan),
      });
      if (mutateReportState) {
        mutateReportState({
          ...reportsState,
          didReportPickerView: true,
        });
      }
    }
  }, []);

  const tabs = (
    <div className={STYLES.INDEX.PLAN_PICKER.TABS}>
      {availablePlans.map((plan) => (
        <LiliButton
          type={LiliButtonType.SECONDARY}
          id={plan.type}
          key={plan.type}
          title={plan.name.replace('Lili ', '')}
          className={selectedPlan === plan ? PLAN_PICKER.SELECTED_TAB : PLAN_PICKER.UNSELECTED_TAB}
          onClick={() => onTabSelectedHandler(plan)}
        />
      ))}
    </div>
  );

  const selectedPlanView = availablePlans.find((plan) => selectedPlan.type === plan.type) || availablePlans[0];

  return (
    <div className='flex flex-col px-[32px] py-[24px] bg-surface-primary-bw-00 rounded-[12px] basis-[70%] h-[95vh] animate-fadeIn min-w-[1000px] overflow-y-scroll'>
      <CloseButton
        className='absolute right-[12px] top-[12px]'
        id='close-button'
        onClick={onCloseClickHandler}
        size={CloseButtonSize.Large}
      />
      <div className='flex justify-center mb-[8px]'>
        <HeadingDS.H5 fontWeight={FontWeight.SEMIBOLD}>{t(TEXT.PLAN_PICKER.TITLE)}</HeadingDS.H5>
      </div>

      <div
        className={twMerge(
          'flex items-center py-[16px]',
          availablePlans.length > 1 ? ' justify-between' : 'justify-center',
        )}
      >
        {availablePlans.length > 1 && tabs}
        {selectedPlanView.annualData && !isAnnualPlanPreselected && (
          <PlansAnnualSwitcher
            isAnnual={isAnnual}
            setIsAnnual={setIsAnnual}
            discountPercentage={selectedPlanView.annualData.discountPercentage}
            currentPlan={currentPlan}
          />
        )}
      </div>
      <PlanView
        key={selectedPlanView.type}
        plan={selectedPlanView}
        onUpgradeClick={onUpgradeClickHandler}
        isAnnual={isAnnual}
      />
      <Heading
        title={selectedPlan?.disclaimer?.replaceAll('\n', '<br/>') || ''}
        className={STYLES.INDEX.PLAN_PICKER.PLAN.DISCLAIMER}
        type='HTML'
      />
    </div>
  );
};
export default PlanPicker;
